import { useEffect, useMemo, useState } from "react"

import { useClearNotificationsMutation, useFetchUserDataQuery, useNotificationsReadMutation, useDeleteUserNotificationMutation } from "../../../app/userApiSlice"
import useAuth from "../../../hooks/useAuth"
import Spinner from "../../../components/Spinner"
import DisplayErrorMessage from "../../../components/DisplayErrorMessage"
import FormattedNotification from "../../../components/notifications/FormattedNotification"
import toast from "react-hot-toast"
import ButtonWithTooltip from "../../../components/buttons/ButtonWithTooltip"
import { Button, Dropdown } from "react-bootstrap"
import { SquareCheckBig } from "lucide-react"

const NotificationsPage = () => {
  const { uid } = useAuth()

  const [deleteUserNotification] = useDeleteUserNotificationMutation()
  const [clearNotifications] = useClearNotificationsMutation()
  const [notificationsRead] = useNotificationsReadMutation()
  const {
    data,
    isLoading,
    isSuccess,
    isError,
    error
  } = useFetchUserDataQuery(uid)

  const [view, setView] = useState("notifications")
  const [showDropdown, setShowDropdown] = useState(false)

  // Clear notifications when the component mounts
  useEffect(() => {
    if (data?.notifications > 0) {
      clearNotifications(uid)
    }
  }, [])

  // Calculate the unread notifications
  const { unreadInvites, unreadMessages } = useMemo(() => {
    if (!data) return { unreadInvites: [], unreadMessages: [] }

    const unreadInvites = Object.entries(data?.invites || {})
      .filter(([_, invite]) => !invite.read)
      .map(([id]) => id)

    const unreadMessages = Object.entries(data?.messages || {})
      .filter(([_, message]) => !message.read)
      .map(([id]) => id)

    return { unreadInvites, unreadMessages }
  }, [data])

  const hasUnread = unreadInvites.length > 0 || unreadMessages.length > 0

  const markAllRead = () => {
    if (hasUnread) {
      notificationsRead({ invites: unreadInvites, messages: unreadMessages, id: uid })
    }
  }

  const markOneRead = (docId, category) => {
    if (category === "invites") {
      notificationsRead({ invites: [docId], messages: [], id: uid })
    } else if (category === "messages") {
      notificationsRead({ invites: [], messages: [docId], id: uid })
    }
  }

  const handleDeleteNotification = async (args) => {
    try {
      await deleteUserNotification(args)
    } catch (error) {
      console.error("Error deleting notification:", error)
      toast.error("Error deleting notification")
    }
  }

  if (isLoading) return <Spinner />
  if (isError) {
    console.error(error || "An error occurred while fetching notifications")
    return <DisplayErrorMessage>Error fetching notifications</DisplayErrorMessage>
  }
  if (!isSuccess || !data) return null

  const invitesArray = data.invites
    ? Object.entries(data.invites).map(([id, inviteData]) => ({
      id,
      category: "invites",
      type: "invite",
      ...inviteData,
    }))
    : []

  const messagesArray = data.messages
    ? Object.entries(data.messages).map(([id, messageData]) => ({
      id,
      category: "messages",
      ...messageData
    }))
    : []

  const mergedArray = [...invitesArray, ...messagesArray]

  const viewMap = {
    notifications: mergedArray,
    invites: invitesArray,
    messages: messagesArray
  }

  const chosenArray = viewMap[view] ?? mergedArray

  const sortedArray = chosenArray.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))

  const listContent = sortedArray.length > 0 ? (
    <ul className="w-100 list-unstyled">
      {sortedArray.map((content) => (
        <FormattedNotification
          key={content.id}
          data={{
            ...content
          }}
          deleteNotification={() => handleDeleteNotification({ docId: content.id, uid, category: content.category })}
          markRead={() => markOneRead(content.id, content.category)}
        />
      ))}
    </ul>
  ) : (
    <p>No {view} to show</p>
  )

  const currentClass = "fw-semibold rounded-3 bg-primary bg-opacity-10 text-primary"
  const inactiveClass = "border-0 fw-semibold rounded-3 text-muted custom-hover"

  return (
    <div
      className="d-flex flex-column align-items-center shadow-sm rounded-4 pb-4 pt-3 px-2 p-sm-4 bg-white"
      style={{ maxWidth: "100%", width: "680px" }}
    >
      <div className="position-relative w-100 mb-4">
        <h3 className="fw-bold text-center mb-0 mt-2">Notifications</h3>

        {/* Mark all as read button */}
        {hasUnread && (
          <ButtonWithTooltip text="Mark all as read">
            <Button onClick={markAllRead} variant="" className="ms-auto border-0 p-2 me-2 position-absolute top-0 end-0"><SquareCheckBig /></Button>
          </ButtonWithTooltip>
        )}
      </div>

      {/* Change view buttons */}
      <div className="justify-content-start w-100 gap-3 ps-2 d-none d-sm-flex">
        <Button onClick={() => setView("notifications")} variant="" className={view === "notifications" ? currentClass : inactiveClass}>All</Button>
        <Button onClick={() => setView("messages")} variant="" className={view === "messages" ? currentClass : inactiveClass}>Messages</Button>
        <Button onClick={() => setView("invites")} variant="" className={view === "invites" ? currentClass : inactiveClass}>Invites</Button>
      </div>

      {/* Dropdown menu for mobile */}
      <div className="d-flex w-100 gap-3 ps-2 d-sm-none">
        <Dropdown show={showDropdown} onToggle={(isOpen) => setShowDropdown(isOpen)} drop="down-end">
          <Dropdown.Toggle
            variant=''
            className={`border-0 ${currentClass} px-3 mb-2`}
          >
            View
          </Dropdown.Toggle>

          <Dropdown.Menu className="shadow border-0 px-2">
            <Dropdown.Item onClick={() => setView("all")} className={`${view === "all" && currentClass} ${inactiveClass} custom-hover mb-1`}>
              All
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setView("messages")} className={`${view === "messages" && currentClass} ${inactiveClass} custom-hover mb-1`}>
              Messages
            </Dropdown.Item>
            <Dropdown.Item onClick={() => setView("invites")} className={`${view === "invites" && currentClass} ${inactiveClass} custom-hover`}>
              Invites
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <hr className="w-100 text-secondary mt-1" />
      {listContent}
    </div>
  )
}

export default NotificationsPage

